import React, { useEffect } from 'react';
import { Newspaper, RefreshCw, AlertCircle, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { fetchLegalNewsThunk } from '../../redux/thunks/legalNewsThunks';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { getLanguageDirection } from '../../i18n/config';
import NewsItem from './NewsItem';
import PanelHeader from '../common/PanelHeader';

const LegalNewsFeed: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { items, loading, error, fetchedAt } = useAppSelector((state) => state.legalNews);
  const user = useAppSelector((state) => state.UserReducer.user);
  const isRTL = getLanguageDirection(i18n.language) === 'rtl';

  // Fetch news when component mounts if not fetched recently
  useEffect(() => {
    // Basic cache logic: refetch if no items, or if fetched more than 1 hour ago
    const shouldFetch =
      !fetchedAt || new Date().getTime() - new Date(fetchedAt).getTime() > 3600000;

    if (shouldFetch && !loading) {
      dispatch(fetchLegalNewsThunk(user?.legalNewsJurisdiction || 'CYPRUS'));
    }
  }, [dispatch, fetchedAt, user?.legalNewsJurisdiction, loading]);

  const handleRefresh = () => {
    dispatch(fetchLegalNewsThunk(user?.legalNewsJurisdiction || 'CYPRUS'));
  };

  return (
    <div
      dir={getLanguageDirection(i18n.language)}
      className="h-full flex flex-col overflow-hidden rounded-none"
    >
      <PanelHeader
        icon={<Newspaper size={20} strokeWidth={1.5} className="text-gold" />}
        title={t('legalNews.title', { defaultValue: 'Legal News' })}
        tooltipText={t('legalNews.tooltip', {
          defaultValue: 'Latest legal updates and announcements from Cyprus legal sources.',
        })}
        subtitle={fetchedAt ? (
          <>
            <RefreshCw size={12} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground">
              {t('legalNews.lastUpdated', { defaultValue: 'Updated' })}{' '}
              {new Date(fetchedAt).toLocaleTimeString(i18n.language, {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          </>
        ) : undefined}
        rightAction={
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleRefresh}
                  disabled={loading}
                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                >
                  <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-sm">
                  {t('legalNews.refresh', { defaultValue: 'Refresh feed' })}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        }
      />

      {/* Content Area */}
      <div className="flex-1 overflow-auto">
        {loading && items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-muted-foreground">
            <RefreshCw size={32} className="animate-spin opacity-40" />
            <p className="text-sm">{t('legalNews.loading', { defaultValue: 'Loading news…' })}</p>
          </div>
        ) : error && items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-muted-foreground p-6">
            <AlertCircle size={40} className="text-destructive opacity-70" />
            <p className="text-sm text-destructive text-center">
              {t('legalNews.error', { defaultValue: 'Failed to load news feed.' })}
            </p>
            <Button variant="outline" size="sm" onClick={handleRefresh}>
              {t('legalNews.retry', { defaultValue: 'Try again' })}
            </Button>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-muted-foreground p-6">
            <Newspaper size={48} className="opacity-30" />
            <p className="text-base font-medium">
              {t('legalNews.empty', { defaultValue: 'No news available' })}
            </p>
            <p className="text-sm text-center">
              {t('legalNews.emptyDesc', {
                defaultValue: 'Check back later for updates from legal sources.',
              })}
            </p>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            {items.map((item) => (
              <NewsItem key={item._id || item.url} item={item} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(LegalNewsFeed);
